/* =====================================================================
   SUSU — variant media filter (multiple images per colour)
   ---------------------------------------------------------------------
   Shows only the gallery images that belong to the selected variant, with
   no limit of one image per variant. Shopify itself allows only ONE image
   to be attached to a variant, so this reads the ALT TEXT instead:

     Green hoodie, front view [[v for="Green"]]
     Green hoodie, back view  [[bp for="Green" x=50 y=36 w=48 ink=#ffffff]]

   Both tags count. An image already tagged for the back-print preview
   needs no second tag — its for= is reused.

   CHOICES. Any other key on the tag names a personalisation DROPDOWN (a
   list-mode `select` field) and the value(s) it must be set to:

     Navy hoodie, front, crest    [[v for="Navy" logo="Crest"]]
     Navy hoodie, front, wordmark [[v for="Navy" logo="Wordmark"]]
     Navy hoodie, back view       [[v for="Navy"]]

   The key is the field's property key, lower-cased, with anything that is
   not a letter turned into "_" (Logo -> logo, Back Logo -> back_logo). A
   choice only narrows: with the dropdown still on "Please choose..." every
   logo shows, and an image with no logo= (the back) ignores the dropdown
   altogether. Keys the engine uses for print boxes (x, y, w, h, ink...) are
   never read as choices.

   Untagged images (size guides, fabric shots) are ALWAYS shown.
   If nothing matches the selection, the gallery is left untouched, so a
   mistyped colour can never produce an empty gallery.

   Where it goes:  assets/susu-variant-media-filter.js, loaded from
                   theme.liquid AFTER susu-personalisation.js (both defer, so
                   document order is execution order) — it reads that file's
                   window.SUSU_ZONES to learn the print markers.

   Turn OFF Horizon's own "Hide media for unselected variants" setting —
   one mechanism should own the decision. Full notes in
   docs/variant-media-filter.md. Add ?vmdebug=1 to a product URL to log
   what was hidden and why.
   ===================================================================== */
(() => {
  'use strict';

  /* ---------- settings ---------- */
  const OPTION_NAMES = null;   // null = match for= against ANY selected option value.
                               // e.g. ['Colour','Color'] to restrict to colour only.
  /* 'v' is this script's own tag. The print markers come from the
   personalisation engine so a new zone is recognised here automatically —
   the fallback is what the list used to be hardcoded to. */
const MARKERS      = ['v'].concat(
  (window.SUSU_ZONES || [{ marker: 'bp' }]).map((z) => z.marker)
);
  const STRIP_ALT    = true;   // remove [[v ...]] from alt so screen readers don't read it
  const SYNC_THUMBS  = true;   // hide matching thumbnails / carousel dots too
  const RESCROLL     = true;   // scroll a carousel back to the first visible slide

  const DEBUG   = /[?&]vmdebug=1/.test(location.search);
  const V_RE    = /\[\[\s*v\b([^\]]*)\]\]/i;                 // the tag this script owns
  const ANY_RE  = new RegExp('\\[\\[\\s*(' + MARKERS.join('|') + ')\\b([^\\]]*)\\]\\]', 'i');
  const FOR_RE  = /\bfor\s*=\s*("[^"]*"|'[^']*'|[^\s\]]+)/i;
  const KV_RE   = /([a-z_]+)\s*=\s*("[^"]*"|'[^']*'|[^\s\]]+)/gi;   // as the engine's
  /* for= and the personalisation engine's print-box keys. Anything else on
     a tag is a choice. */
  const NOT_CHOICES = ['for', 'x', 'y', 'w', 'h', 'size', 'ink', 'font', 'weight',
    'spacing', 'lh', 'rotate', 'case', 'shadow', 'opacity'];

  const hidden    = new Set();   // elements THIS script hid — never touch anything else
  const prevStyle = new WeakMap();
  let   scheduled = false;
  let   passing   = false;

  /* ===================== small helpers ===================== */

  const norm = (v) => String(v == null ? '' : v).trim().toLowerCase();
  const log  = (...a) => { if (DEBUG) console.log('[susu-vm]', ...a); };

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; pass(); });
  }

  /* ===================== alt-text config ===================== */

  /* The colour(s) an image is tagged for, or null if untagged.
     Cached on the element, because the back-print script strips its own
     [[bp ...]] tag out of the alt text once it has read it — when that has
     already happened we recover the value from the cache it leaves behind. */
  function tagFor(img) {
    if ('susuVmFor' in img.dataset) {
      return img.dataset.susuVmFor === '' ? null : img.dataset.susuVmFor;
    }

    const alt = img.getAttribute('alt') || '';
    /* The personalisation engine keeps the alt as written in
       data-susu-alt-raw before stripping its own tag, and its parsed cache
       drops keys it does not use -- so the raw copy is the only place a
       choice survives once the engine has been at the image first. */
    const source = img.dataset.susuAltRaw || alt;
    let value = null;
    const choices = {};

    const m = ANY_RE.exec(source);
    if (m) {
      const body = m[2] || '';
      const f = FOR_RE.exec(body);
      // Tag present but no for= -> deliberately colour-agnostic, treat as untagged.
      value = f ? f[1].replace(/^['"]|['"]$/g, '') : null;
      let kv;
      KV_RE.lastIndex = 0;
      while ((kv = KV_RE.exec(body)) !== null) {
        const key = kv[1].toLowerCase();
        if (NOT_CHOICES.indexOf(key) !== -1) continue;
        choices[key] = kv[2].replace(/^['"]|['"]$/g, '').split(',').map(norm).filter(Boolean);
      }
      } else {
        /* The personalisation engine strips its own [[bp ...]] / [[fp ...]]
           tag from alt once it has read it, caching the parsed config on the
           element. Recover for= from whichever zone cached one. */
        for (const marker of MARKERS) {
          const raw = img.getAttribute('data-susu-cfg-' + marker);
          if (!raw || raw === '0') continue;
          try {
            const cfg = JSON.parse(raw);
            if (cfg && cfg.for) { value = String(cfg.for); break; }
          } catch (e) { /* ignore */ }
        }
      }

    img.dataset.susuVmFor = value == null ? '' : value;
    img.dataset.susuVmChoices = Object.keys(choices).length ? JSON.stringify(choices) : '';

    if (STRIP_ALT && V_RE.test(alt)) {
      img.setAttribute(
        'alt',
        alt.replace(V_RE, '').replace(/\s{2,}/g, ' ').replace(/\s+([,.])/g, '$1').trim()
      );
    }
    return value;
  }

  function matches(value, selected) {
    if (!value) return true;              // untagged -> always visible
    if (!selected.size) return true;      // nothing selected yet -> show everything
    return String(value).split(',').some((c) => selected.has(norm(c)));
  }

  /* {key: [values]} this image's choices, or null. Always after tagFor. */
  function choicesFor(img) {
    tagFor(img);
    const raw = img.dataset.susuVmChoices;
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) { return null; }
  }

  /* Tagged at all: a colour, a choice, or both. */
  function isTagged(img) {
    return !!(tagFor(img) || choicesFor(img));
  }

  /* An image passes when every choice it names is either not yet made
     (dropdown blank), not on this page (no such dropdown), or made to one of
     its values. So a choice can only ever narrow the gallery. */
  function choicesMatch(choices, chosen) {
    if (!choices) return true;
    return Object.keys(choices).every((key) => {
      const pick = chosen.get(key);
      return !pick || choices[key].indexOf(pick) !== -1;
    });
  }

  /* Property key -> tag key: "Logo" -> "logo", "Back Logo" -> "back_logo". */
  const choiceKey = (property) => norm(property).replace(/[^a-z]+/g, '_').replace(/^_+|_+$/g, '');

  /* The personalisation dropdowns in this product: key -> chosen value.
     Rendered by blocks/susu-personalisation-list.liquid as
     <select data-susu-property="Logo">. */
  function chosenValues(root) {
    const out = new Map();
    root.querySelectorAll('select[data-susu-property]').forEach((sel) => {
      const key = choiceKey(sel.getAttribute('data-susu-property'));
      if (key && sel.value) out.set(key, norm(sel.value));
    });
    return out;
  }

  /* ===================== variant state ===================== */

  /* Every option value currently selected in a picker. */
  function selectedValues(picker) {
    const out = new Set();
    if (!picker) return out;
    picker.querySelectorAll('[data-option-name]').forEach((node) => {
      if (OPTION_NAMES && OPTION_NAMES.map(norm).indexOf(norm(node.dataset.optionName)) === -1) return;
      let val = null;
      if (node instanceof HTMLInputElement) { if (node.checked) val = node.value; }
      else if (node instanceof HTMLOptionElement) { if (node.selected) val = node.value; }
      else if (node instanceof HTMLSelectElement) { val = node.value; }
      if (val) out.add(norm(val));
    });
    return out;
  }

  /* The smallest ancestor of the picker that also holds tagged images.
     Keeps a quick-add modal or a second product section from filtering
     the wrong gallery. */
  function rootFor(picker) {
    let el = picker.parentElement;
    for (let i = 0; el && i < 12; i++) {
      if (taggedIn(el).length) return el;
      if (el === document.body) break;
      el = el.parentElement;
    }
    return null;
  }

  function taggedIn(root) {
    const out = [];
    root.querySelectorAll('img[alt]').forEach((img) => {
      if (isTagged(img)) out.push(img);
    });
    return out;
  }

  /* ===================== slide resolution ===================== */

  const imgCount = (el) => el.querySelectorAll('img').length;

  /* The largest wrapper around this image that contains no OTHER image —
     i.e. the slide / grid cell / thumbnail button, whatever the theme calls
     it. Never returns something that would take another image down with it. */
  const STOP_AT = 'dialog, [role="dialog"], [class*="modal"], form, variant-picker, product-form-component';

  function slideOf(img, root) {
    let el = img;
    for (let i = 0; i < 8; i++) {
      const p = el.parentElement;
      if (!p || p === root || p === document.body || !root.contains(p)) break;
      if (p.matches && p.matches(STOP_AT)) break;   // never hide a whole modal/form
      if (imgCount(p) !== 1) break;                 // parent brings in another image
      el = p;
    }
    return el;
  }

  function hide(el, why) {
    if (hidden.has(el)) return;
    prevStyle.set(el, el.style.display);
    el.style.display = 'none';
    el.setAttribute('data-susu-vm-hidden', '');
    hidden.add(el);
    log('hide', why, el);
  }

  function show(el) {
    /* Also un-hides an element carrying our marker that we are not tracking:
       a theme morph or a cloned lightbox can copy the hidden state onto a
       brand-new node, and nothing else would ever clear it. */
    if (!hidden.has(el) && !el.hasAttribute('data-susu-vm-hidden')) return;
    const prev = prevStyle.get(el);
    el.style.display = prev == null ? '' : prev;
    el.removeAttribute('data-susu-vm-hidden');
    hidden.delete(el);
  }

  /* Anything left marked from a previous render that we no longer track. */
  function reclaim(root) {
    root.querySelectorAll('[data-susu-vm-hidden]').forEach((el) => {
      if (!hidden.has(el)) show(el);
    });
  }

  /* Carousel dots / counters: a control container whose child count equals the
     slide count. Best effort only — wrapped so a theme change can't throw. */
  function syncControls(root, slides, keep) {
    if (!SYNC_THUMBS) return;
    try {
      const total = slides.length;
      if (total < 2) return;
      const pools = root.querySelectorAll(
        'slideshow-controls, [class*="slideshow-control"], [class*="dots"], [class*="indicator"], [class*="thumbnail"]'
      );
      pools.forEach((pool) => {
        const kids = Array.prototype.filter.call(pool.children, (c) => !c.hasAttribute('data-susu-vm-skip'));
        if (kids.length !== total) return;
        kids.forEach((kid, i) => (keep.has(slides[i]) ? show(kid) : hide(kid, 'control')));
      });
    } catch (e) {
      log('control sync skipped', e);
    }
  }

  function rescroll(root, keep) {
    if (!RESCROLL) return;
    try {
      const first = Array.from(keep)[0];
      if (!first) return;
      let el = first.parentElement;
      for (let i = 0; el && i < 6; i++) {
        if (el.scrollWidth > el.clientWidth + 4) {
          const target = first.offsetLeft - el.offsetLeft;
          if (Math.abs(el.scrollLeft - target) > 4) el.scrollLeft = target;
          return;
        }
        el = el.parentElement;
      }
    } catch (e) { /* ignore */ }
  }

  /* ===================== main pass ===================== */

  function pass() {
    if (passing) return;
    passing = true;
    try { run(); } catch (e) { console.error('[susu-vm]', e); } finally { passing = false; }
  }

  function run() {
    const pickers = document.querySelectorAll('variant-picker');
    if (!pickers.length) {
      hidden.forEach(show);
      reclaim(document.documentElement);
      return;
    }

    const seen = new Set();
    let primary = null;

    pickers.forEach((picker) => {
      const root = rootFor(picker);
      if (!root || seen.has(root)) return;
      seen.add(root);
      if (!primary) primary = { picker, root };
      filter(root, selectedValues(picker), chosenValues(root));
    });

    /* Tagged images living outside every root — zoom/lightbox dialogs,
       sticky galleries — follow the first product on the page. */
    if (primary) {
      const stray = [];
      document.querySelectorAll('img[alt]').forEach((img) => {
        if (!isTagged(img)) return;
        for (const r of seen) if (r.contains(img)) return;
        stray.push(img);
      });
      if (stray.length) {
        filter(document.body, selectedValues(primary.picker), chosenValues(primary.root), stray);
      }
    }
  }

  function filter(root, selected, chosen, only) {
    const imgs = only || Array.prototype.slice.call(root.querySelectorAll('img[alt]'));
    let plan = planFor(root, imgs, selected, chosen);

    /* A choice with no photo for this colour (Red selected, only a Navy
       wordmark shot exists) must not blank the colour: drop the choices and
       filter on colour alone -- exactly the behaviour before choices existed. */
    if (chosen.size && plan.anyTagged && !plan.anyMatched) {
      log('no image for', Array.from(chosen.values()).join(', '), '— ignoring choices');
      plan = planFor(root, imgs, selected, new Map());
    }

    /* Nothing is tagged, or nothing tagged matches the current selection:
       leave the gallery exactly as the theme rendered it. A mistyped colour
       shows too much, never too little. */
    if (!plan.anyTagged || !plan.anyMatched) {
      if (plan.anyTagged) log('nothing tagged matches', Array.from(selected).join(', '), '— showing all');
      plan.slides.forEach(show);
      reclaim(root);
      return;
    }

    plan.slides.forEach((slide) => (plan.keep.has(slide) ? show(slide) : hide(slide, 'unselected')));
    reclaim(root);
    syncControls(root, plan.slides, plan.keep);
    rescroll(root, plan.keep);
  }

  /* Which slides this selection keeps. Reads only -- changes nothing -- so
     filter() can try once with the choices and once without. */
  function planFor(root, imgs, selected, chosen) {
    const slides = [];
    const keep = new Set();
    const map = new Map();          // slide -> does any image in it match
    let anyTagged = false;          // is anything here colour- or choice-specific at all
    let anyMatched = false;         // did such an image match the selection

    imgs.forEach((img) => {
      const value = tagFor(img);
      const choices = choicesFor(img);
      const tagged = !!(value || choices);
      const slide = slideOf(img, root);
      if (!map.has(slide)) { map.set(slide, false); slides.push(slide); }
      if (tagged) anyTagged = true;
      if (matches(value, selected) && choicesMatch(choices, chosen)) {
        map.set(slide, true);
        if (tagged) anyMatched = true;
      }
    });

    map.forEach((ok, slide) => { if (ok) keep.add(slide); });
    return { slides, keep, anyTagged, anyMatched };
  }

  /* ===================== wiring ===================== */

  function start() {
    document.addEventListener('change', (e) => {
      const t = e.target;
      if (t && t.closest && (t.closest('variant-picker') || t.closest('[data-susu-property]'))) schedule();
    });
    document.addEventListener('click', (e) => {
      const t = e.target;
      if (t && t.closest && t.closest('variant-picker')) schedule();
    }, true);

    window.addEventListener('resize', schedule);
    document.addEventListener('shopify:section:load', schedule);

    /* Horizon re-renders the product section on variant change; a zoom
       dialog clones gallery markup when it opens. Both land here. */
    /* 'style' is deliberately NOT observed: this script hides by setting
       display, and watching style would make it retrigger itself. */
    const mo = new MutationObserver(() => schedule());
    mo.observe(document.documentElement, {
      childList: true, subtree: true,
      attributes: true, attributeFilter: ['alt', 'checked', 'selected', 'aria-checked', 'aria-selected']
    });

    schedule();
    [150, 500, 1200].forEach((ms) => setTimeout(schedule, ms));
  }

  /* First pass runs immediately — the gallery is already parsed at this
     point in the body, so the unfiltered images never reach the screen. */
  pass();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
